import { StringFieldModel } from "@/common/commonForms";
import { orderlessFilter } from "@/utils/filter";
import { isOk } from "@/common/common";
import { getRoleTypeLabel, RoleType } from "@/types/enums/RoleType";
import { TMessage, TMessageParam } from "@/i18n/tt";
import { DataTableProps } from "@/components-v2/DataTable";
import { navigateToAggregatedCompanyRoleDetails } from "./aggregatedContactDetailsDomain";
import { SelectedPortfolioOption } from "@/portfolio/portfolioDomain";
import { DCEvent, EventDispatcher, navigateTo } from "@/events/eventsDomain";
import { DropdownMenuItemType, DropdownMenuProps } from "@/components-v2/Dropdown";
import { isBlank, isNotBlank } from "@/utils/strings";
import { sortBy, uniq, uniqBy } from "lodash";
import {
  AggregatedContactItem,
  ContactsState,
  RoleItem,
  updateContactsState,
} from "./contactsDomain";
import { TextWithTooltipProps } from "@/components-v2/TextWithTooltip";
import { formatDate } from "@/i18n/dateFormat.ts";
import { Language } from "@/i18n/languages.ts";
import { CompanyRole } from "@/types/CompanyInfo.ts";
import { TMessageKey } from "@/i18n/types.gen.ts";

export type CompanyRolesViewProps = {
  aggregatedPersonItems: AggregatedContactItem[];
  roleNameFilter?: StringFieldModel;
  filterMenus: FilterMenu[];
  navigateToDetails: (id: string | number) => void;
  resetFilters?: () => void;
  rowActions: DataTableProps<AggregatedContactItem>["rowActions"];
};

export type StatusColumn = "nationality" | "countryOfResidence" | "email" | "lastLogin";

export type StatusFilterValue = ValueStatus | { country: string };
export type StatusFilters = Record<StatusColumn, StatusFilterValue | null>;

export type CompanyRoleFilters = {
  companyOrgNo?: string;
  roleType?: RoleType;
  companySearch: string;
  statuses: StatusFilters;
};

export const noCompanyRoleFilters: CompanyRoleFilters = {
  companySearch: "",
  statuses: { nationality: null, countryOfResidence: null, email: null, lastLogin: null },
};

export type FilterMenu = { name: "company" | "roleType" | StatusColumn; menu: DropdownMenuProps };
type SetFilters = (change: (filters: CompanyRoleFilters) => Partial<CompanyRoleFilters>) => void;

type FilterContext = {
  rows: AggregatedContactItem[];
  filters: CompanyRoleFilters;
  setFilters: SetFilters;
};

export const setCompanyRoleFilters = (
  change: (filters: CompanyRoleFilters) => Partial<CompanyRoleFilters>,
): DCEvent =>
  updateContactsState((state) => {
    const filters = state?.companyRoleFilters ?? noCompanyRoleFilters;
    return { companyRoleFilters: { ...filters, ...change(filters) } };
  });

export const prepCompanyRolesView = ({
  state,
  dispatch,
  portfolio,
}: {
  state: ContactsState;
  dispatch: EventDispatcher;
  portfolio: SelectedPortfolioOption;
}): CompanyRolesViewProps => {
  const { response } = state;
  const rows = isOk(response)
    ? response.data.contacts.filter((item) => item.roleItems.length > 0)
    : [];
  const filters = withoutUnknownCompany(state.companyRoleFilters ?? noCompanyRoleFilters, rows);
  const setFilters: SetFilters = (change) => dispatch([setCompanyRoleFilters(change)]);
  const context: FilterContext = { rows, filters, setFilters };

  return {
    aggregatedPersonItems: filterBySelectedMenus(rows, filters),
    roleNameFilter: state.roleNameFilter,
    filterMenus: [
      { name: "company", menu: companyMenu(context) },
      { name: "roleType", menu: roleTypeMenu(context) },
      ...statusColumnNames.map((column) => ({ name: column, menu: statusMenu(column, context) })),
    ],
    navigateToDetails: (companyroleId) =>
      dispatch(
        navigateToAggregatedCompanyRoleDetails({
          id: companyroleId,
          portfolio,
        }),
      ),
    resetFilters: hasActiveFilters({ ...state, companyRoleFilters: filters })
      ? () =>
          dispatch([
            updateContactsState(() => ({
              companyRoleFilters: noCompanyRoleFilters,
              roleNameFilter: new StringFieldModel(),
            })),
          ])
      : undefined,
    rowActions: (row) => {
      return [
        [
          "context-menu",
          [
            {
              type: "item",
              label: { key: "contacts.company-roles.view-details" },
              icon: "user-circle",
              onSelect: () =>
                dispatch(
                  navigateToAggregatedCompanyRoleDetails({
                    id: row.aggregatedContactId,
                    portfolio,
                  }),
                ),
            },
            { type: "label", label: { key: "contacts.company-roles-go-to-company" } },
            ...row.roleItems.map<DropdownMenuItemType>((c) => ({
              type: "item",
              label: c.company.companyName,
              icon: "eye",
              onSelect: () =>
                dispatch([
                  navigateTo({
                    name: "admin/roles",
                    params: { orgNo: c.company.organisationNumber },
                  }),
                ]),
            })),
          ],
        ],
      ];
    },
  };
};

export const hasActiveFilters = (state: ContactsState): boolean => {
  const filters = state.companyRoleFilters ?? noCompanyRoleFilters;
  return (
    isNotBlank(state.roleNameFilter?.inputValue) ||
    Boolean(filters.companyOrgNo) ||
    Boolean(filters.roleType) ||
    statusColumnNames.some((column) => filters.statuses[column] !== null)
  );
};

export const companyRolesRoleType = (items: RoleItem[]): TextWithTooltipProps => {
  const countsMap = new Map<RoleType, number>();
  for (const item of items) {
    countsMap.set(item.role.role, (countsMap.get(item.role.role) || 0) + 1);
  }

  const groupedWithCounts = Array.from(countsMap.entries()).map(([roleType, count]) => ({
    roleType,
    count,
  }));

  if (groupedWithCounts.length === 1) {
    return {
      text: getRoleTypeLabel(groupedWithCounts[0].roleType),
    };
  }
  return {
    text: { key: "contacts.multiple" },
    tooltip: {
      type: "md-list",
      value: groupedWithCounts.map(({ roleType, count }) => ({
        key: "contacts.company-roles.x-with-count",
        params: {
          x: getRoleTypeLabel(roleType),
          count,
        },
      })),
    },
  };
};

export function companyRolesCompanies(roleItems: RoleItem[]): TextWithTooltipProps {
  const companyItems = roleItems.map((c) => c.company);
  if (companyItems.length === 1) {
    return { text: { translated: companyItems[0].companyName } };
  }
  return {
    tooltip: { type: "md-list", value: companyItems.map((c) => c.companyName) },
    text: { key: "portfolio.numberOfCompanies", params: { count: companyItems.length } },
  };
}

export function companyRolesValidFrom(
  roleItems: RoleItem[],
  language: Language,
): TextWithTooltipProps {
  const validFroms = roleItems.map((i) => i.role.validFrom);
  const notSet = validFroms.filter(isBlank);
  if (notSet.length === roleItems.length) return { text: { key: "not-set" }, hasNotSet: true };
  const tooltip: TMessageParam = {
    type: "md-list",
    value: roleItems.map((i, index) => ({
      type: "list",
      separator: " - ",
      value: [
        i.company.companyName,
        getRoleTypeLabel(i.role.role),
        isBlank(validFroms[index])
          ? { key: "not-set" }
          : { type: "date", value: validFroms[index] },
      ],
    })),
  };
  if (notSet.length > 0) return { tooltip, text: { key: "contacts.incomplete" } };
  if (roleItems.length === 1) return { text: { translated: formatDate(language, validFroms[0]) } };
  return { tooltip, text: { key: "contacts.multiple" } };
}

export const companyRolesNationality = (roleItems: RoleItem[]): TextWithTooltipProps =>
  companyRolesCountry(
    roleItems,
    (role) => role.nationality,
    "contacts.company-roles.nationality-missing",
  );

export const companyRolesCountryOfResidence = (roleItems: RoleItem[]): TextWithTooltipProps =>
  companyRolesCountry(
    roleItems,
    (role) => role.residentialAddress?.country,
    "contacts.company-roles.residence-missing",
  );

function companyRolesCountry(
  roleItems: RoleItem[],
  getCountry: (role: CompanyRole) => string | undefined,
  missingKey: TMessageKey,
): TextWithTooltipProps {
  const countries = roleItems.map((i) => getCountry(i.role));
  const status = valueStatus(countries);
  if (status === "notSet")
    return { text: { key: "not-set" }, hasNotSet: true, tooltip: { key: missingKey } };
  const tooltip: TMessageParam = {
    type: "md-list",
    value: roleItems.map((i, index) => ({
      type: "list",
      separator: " - ",
      value: [
        i.company.companyName,
        getRoleTypeLabel(i.role.role),
        countries[index] || { key: "not-set" },
      ],
    })),
  };
  if (status === "incomplete") return { tooltip, text: { key: "contacts.incomplete" } };
  if (status === "set") return { text: { translated: countries[0] ?? "" } };
  return { tooltip, text: { key: "contacts.multiple" } };
}

export type ValueStatus = "notSet" | "incomplete" | "multiple" | "set";

const valueStatus = (values: (string | undefined)[]): ValueStatus => {
  const notSetCount = values.filter(isBlank).length;
  if (notSetCount === values.length) return "notSet";
  if (notSetCount > 0) return "incomplete";
  if (values.length === 1) return "set";
  return "multiple";
};

const companyRolesEmailStatus = (roleItems: RoleItem[]): ValueStatus => {
  const notSetCount = roleItems.filter((f) => isBlank(f.role.email)).length;
  if (notSetCount === roleItems.length) return "notSet";
  if (notSetCount > 0) return "incomplete";
  const emails = roleItems.map((e) => e.role.email);
  return emails.every((email) => email === emails[0]) ? "set" : "multiple";
};

export function companyRolesEmail(roleItems: RoleItem[]): TextWithTooltipProps {
  const rolesEmailNotSet = roleItems.filter((f) => isBlank(f.role.email));
  switch (companyRolesEmailStatus(roleItems)) {
    case "notSet":
      return {
        tooltip: {
          key: "contacts.email-not-set",
          params: {
            count: rolesEmailNotSet.length,
          },
        },
        text: { key: "not-set" },
      };
    case "set":
      return {
        text: { translated: roleItems.find((i) => i.role.email !== undefined)?.role.email ?? "" },
      };
    case "incomplete":
      return {
        tooltip: {
          key: "contacts.incomplete-email",
          params: {
            count: rolesEmailNotSet.length,
            name:
              rolesEmailNotSet.length === 1 ? rolesEmailNotSet[0].company.companyName : undefined,
          },
        },
        text: { key: "contacts.incomplete" },
      };
    case "multiple":
      return {
        tooltip: { key: "contacts.multiple-emails-found" },
        text: { key: "contacts.multiple" },
      };
  }
}

type FilterMenuItem = {
  label: TMessage | TMessageParam | string;
  count: number;
  onSelect: () => void;
};

export const filterMenu = ({
  buttonLabel,
  allLabel,
  total,
  onShowAll,
  groups,
  search,
}: {
  buttonLabel: TMessage | TMessageParam | string;
  allLabel: TMessage;
  total: number;
  onShowAll: () => void;
  groups: FilterMenuItem[][];
  search?: { text: string; onChange: (text: string) => void };
}): DropdownMenuProps => ({
  button: { type: "select", label: buttonLabel, compact: true },
  ...(search && {
    filter: search.text,
    filterPlaceholder: { key: "search" },
    onFilterChange: search.onChange,
    onOpenChange: (open: boolean) => {
      if (!open && search.text !== "") search.onChange("");
    },
  }),
  items: [
    filterMenuItem({ label: allLabel, count: total, onSelect: onShowAll }),
    ...groups
      .filter((group) => group.length > 0)
      .flatMap((group, index): DropdownMenuItemType[] => [
        ...(index > 0 ? [{ type: "separator" as const }] : []),
        ...group.map(filterMenuItem),
      ]),
  ],
});

const filterMenuItem = ({ label, count, onSelect }: FilterMenuItem): DropdownMenuItemType => ({
  type: "item",
  label,
  tag: { label: String(count), color: "neutral", compact: true, className: "ml-auto" },
  onSelect,
});

const rowsMatchingOtherFilters = (
  { rows, filters }: FilterContext,
  ownFilter: Partial<CompanyRoleFilters>,
): AggregatedContactItem[] => filterBySelectedMenus(rows, { ...filters, ...ownFilter });

const companyMenu = (context: FilterContext): DropdownMenuProps => {
  const { rows, filters, setFilters } = context;
  const counted = rowsMatchingOtherFilters(context, { companyOrgNo: undefined });
  const companies = sortBy(
    uniqBy(
      rows.flatMap((row) => row.roleItems.map((i) => i.company)),
      "organisationNumber",
    ),
    "companyName",
  );
  const selected = companies.find((c) => c.organisationNumber === filters.companyOrgNo);
  const select = (companyOrgNo?: string) => setFilters(() => ({ companyOrgNo, companySearch: "" }));
  return filterMenu({
    buttonLabel: selected?.companyName ?? { key: "portfolio.all-companies" },
    allLabel: { key: "portfolio.all-companies" },
    total: counted.length,
    onShowAll: () => select(undefined),
    groups: [
      orderlessFilter(
        companies,
        (c) => [c.companyName, c.organisationNumber],
        filters.companySearch,
      ).map((company) => ({
        label: company.companyName,
        count: counted.filter((row) => rowMatchesCompany(row, company.organisationNumber)).length,
        onSelect: () => select(company.organisationNumber),
      })),
    ],
    search: {
      text: filters.companySearch,
      onChange: (companySearch) => setFilters(() => ({ companySearch })),
    },
  });
};

const roleTypeMenu = (context: FilterContext): DropdownMenuProps => {
  const { rows, filters, setFilters } = context;
  const counted = rowsMatchingOtherFilters(context, { roleType: undefined });
  return filterMenu({
    buttonLabel: filters.roleType ? getRoleTypeLabel(filters.roleType) : { key: "roles.all" },
    allLabel: { key: "roles.all" },
    total: counted.length,
    onShowAll: () => setFilters(() => ({ roleType: undefined })),
    groups: [
      uniq(rows.flatMap((row) => row.roleItems.map((i) => i.role.role))).map((roleType) => ({
        label: getRoleTypeLabel(roleType),
        count: counted.filter((row) => rowMatchesRoleType(row, roleType)).length,
        onSelect: () => setFilters(() => ({ roleType })),
      })),
    ],
  });
};

const statusMenu = (column: StatusColumn, context: FilterContext): DropdownMenuProps => {
  const { rows, filters, setFilters } = context;
  const counted = rowsMatchingOtherFilters(context, {
    statuses: { ...filters.statuses, [column]: null },
  });
  const { label, status, options, optionLabels, countries } = statusColumns[column];
  const selected = filters.statuses[column];
  const optionLabel = (option: ValueStatus) => optionLabels?.[option] ?? statusOptionLabels[option];
  const select = (value: StatusFilterValue | null) =>
    setFilters((current) => ({ statuses: { ...current.statuses, [column]: value } }));
  const statuses = counted.map((row) => status(row.roleItems));
  return filterMenu({
    buttonLabel:
      selected === null
        ? label
        : typeof selected === "string"
          ? optionLabel(selected)
          : { type: "country", code: selected.country },
    allLabel: { key: "showAll" },
    total: counted.length,
    onShowAll: () => select(null),
    groups: [
      options.map((option) => ({
        label: optionLabel(option),
        count: statuses.filter((s) => s === option).length,
        onSelect: () => select(option),
      })),
      countryCounts(rows, counted, countries).map(({ code, count }) => ({
        label: { type: "country", code },
        count,
        onSelect: () => select({ country: code }),
      })),
    ],
  });
};

const countryCounts = (
  rows: AggregatedContactItem[],
  counted: AggregatedContactItem[],
  countriesOf?: (roleItems: RoleItem[]) => (string | undefined)[],
): { code: string; count: number }[] => {
  if (!countriesOf) return [];
  const countriesPerRow = (someRows: AggregatedContactItem[]) =>
    someRows.map((row) => uniq(countriesOf(row.roleItems).filter(isNotBlank)));
  const countedCountries = countriesPerRow(counted);
  return sortBy(uniq(countriesPerRow(rows).flat())).map((code) => ({
    code,
    count: countedCountries.filter((countries) => countries.includes(code)).length,
  }));
};

const statusOptionLabels: Record<ValueStatus, TMessage> = {
  notSet: { key: "not-set" },
  incomplete: { key: "contacts.incomplete" },
  multiple: { key: "contacts.multiple" },
  set: { key: "company-roles.has-logged-in" },
};

const statusOptions: ValueStatus[] = ["notSet", "incomplete", "multiple"];

const nationalities = (roleItems: RoleItem[]) => roleItems.map((i) => i.role.nationality);
const countriesOfResidence = (roleItems: RoleItem[]) =>
  roleItems.map((i) => i.role.residentialAddress?.country);

const statusColumns: Record<
  StatusColumn,
  {
    label: TMessage;
    status: (roleItems: RoleItem[]) => ValueStatus;
    options: ValueStatus[];
    optionLabels?: Partial<Record<ValueStatus, TMessage>>;
    countries?: (roleItems: RoleItem[]) => (string | undefined)[];
  }
> = {
  nationality: {
    label: { key: "shareholders.detailed.nationality" },
    status: (roleItems) => valueStatus(nationalities(roleItems)),
    options: statusOptions,
    countries: nationalities,
  },
  countryOfResidence: {
    label: { key: "company-roles.country-of-residence" },
    status: (roleItems) => valueStatus(countriesOfResidence(roleItems)),
    options: statusOptions,
    countries: countriesOfResidence,
  },
  email: { label: { key: "email" }, status: companyRolesEmailStatus, options: statusOptions },
  lastLogin: {
    label: { key: "last-login" },
    status: (roleItems) => (roleItems.some((i) => isNotBlank(i.role.lastLogin)) ? "set" : "notSet"),
    options: ["notSet", "set"],
    optionLabels: { notSet: { key: "never-logged-in.short" } },
  },
};

const statusColumnNames: StatusColumn[] = [
  "nationality",
  "countryOfResidence",
  "email",
  "lastLogin",
];

const rowMatchesStatus = (
  row: AggregatedContactItem,
  column: StatusColumn,
  selected: StatusFilterValue | null,
): boolean => {
  if (selected === null) return true;
  if (typeof selected === "string") return statusColumns[column].status(row.roleItems) === selected;
  return (statusColumns[column].countries?.(row.roleItems) ?? []).includes(selected.country);
};

const withoutUnknownCompany = (
  filters: CompanyRoleFilters,
  rows: AggregatedContactItem[],
): CompanyRoleFilters =>
  filters.companyOrgNo && !rows.some((row) => rowMatchesCompany(row, filters.companyOrgNo))
    ? { ...filters, companyOrgNo: undefined }
    : filters;

const rowMatchesCompany = (row: AggregatedContactItem, orgNo?: string): boolean =>
  !orgNo || row.roleItems.some((i) => i.company.organisationNumber === orgNo);

const rowMatchesRoleType = (row: AggregatedContactItem, roleType?: RoleType): boolean =>
  !roleType || row.roleItems.some((i) => i.role.role === roleType);

export const filterBySelectedMenus = (
  rows: AggregatedContactItem[],
  filters: CompanyRoleFilters,
): AggregatedContactItem[] =>
  rows.filter(
    (row) =>
      rowMatchesCompany(row, filters.companyOrgNo) &&
      rowMatchesRoleType(row, filters.roleType) &&
      statusColumnNames.every((column) => rowMatchesStatus(row, column, filters.statuses[column])),
  );
